/**
 * Checkout and payment history.
 *
 * Checkout returns a URL on the provider's domain. The browser is sent there;
 * this app never sees card details, and there is nothing here that could
 * collect them. The success and cancel URLs are absolute, because the provider
 * redirects the browser to them from outside this origin, and they are built
 * from the origin the rider is actually on rather than a configured one.
 */

import { api, LIST_ID } from "../api";
import type { Checkout, Meta, Payment, UUID } from "../types";
import type { EnvelopeMeta } from "../baseQuery";
import { appOrigin } from "../../lib/env";
import { routes } from "../../lib/routes";

export interface PaymentPage {
    items: Payment[];
    meta?: Meta;
}

/**
 * Builds the absolute return URLs a provider redirect needs.
 *
 * The paths come from `routes`, which the router reads too, so a URL sent to a
 * provider always names a path this app actually serves.
 */
export function checkoutReturnUrls(bookingId: UUID): {
    successUrl: string;
    cancelUrl: string;
} {
    const origin = appOrigin();
    const query = `?booking=${encodeURIComponent(bookingId)}`;
    return {
        successUrl: `${origin}${routes.paymentSuccess}${query}`,
        cancelUrl: `${origin}${routes.paymentFailed}${query}`,
    };
}

export const paymentsApi = api.injectEndpoints({
    endpoints: (build) => ({
        /** Requires a verified email address; the API refuses otherwise. */
        checkout: build.mutation<Checkout, { bookingId: UUID }>({
            query: ({ bookingId }) => ({
                url: "payments/checkout",
                method: "POST",
                body: { bookingId, ...checkoutReturnUrls(bookingId) },
            }),
            invalidatesTags: [{ type: "Payment", id: LIST_ID }],
        }),

        payment: build.query<Payment, UUID>({
            query: (paymentId) => `payments/${paymentId}`,
            providesTags: (_r, _e, paymentId) => [
                { type: "Payment", id: paymentId },
            ],
        }),

        myPayments: build.query<PaymentPage, { page?: number; limit?: number } | void>({
            query: (args) => ({ url: "payments/mine", params: args ?? {} }),
            transformResponse: (rows: Payment[] | null, meta: EnvelopeMeta) => ({
                items: rows ?? [],
                meta: meta?.envelope?.meta,
            }),
            providesTags: [{ type: "Payment", id: LIST_ID }],
        }),
    }),
});

export const { useCheckoutMutation, usePaymentQuery, useMyPaymentsQuery } =
    paymentsApi;
